<template>
  <div class="relative h-full p-0">
    <div class="absolute z-10 w-full h-full bg-black/50">
      <img
        src="/backgrounds/gradient-03.png"
        alt="Spectra - Gradient Background"
        class="object-cover w-full h-full blur-xl" />
    </div>
    <div class="relative z-20 flex h-full py-8 mb-8">
      <div
        class="flex flex-col flex-1 max-w-sm min-h-full px-6 py-12 mx-auto select-none sm:max-w-full sm:mx-0 sm:justify-center md:px-6 lg:px-8">
        <div class="sm:mx-auto sm:w-full sm:max-w-sm">
          <!-- Back button when redirected -->
          <div v-if="!showCheckEmail" class="mb-1 sm:mb-4">
            <button
              class="inline-flex items-center text-gray-300 transition-colors cursor-pointer hover:text-white hover:underline"
              @click="goBack">
              <fa-icon icon="arrow-left" class="mr-2 text-sm" />
              <span class="text-sm">Back</span>
            </button>
          </div>

          <div
            v-if="showCheckEmail"
            class="flex flex-col p-6 space-y-2 text-sm rounded bg-white/5">
            <p class="text-pretty">Check your email for a 6 digit login code</p>
            <p v-if="isInOtpCooldown" class="text-xs text-gray-400">
              Code sent. You can request a new code in {{ otpCooldownRemaining }}s
            </p>
            <a
              href="#"
              class="italic opacity-75 cursor-pointer hover:underline"
              @click.prevent="resetForm"
              ><fa-icon icon="arrow-left" /> Reset</a
            >
          </div>
          <template v-else>
            <h2
              class="my-2 text-3xl font-bold leading-9 tracking-tight text-center text-white underline sm:my-10 sm:text-4xl lg:text-6xl">
              {{ formTitle }}
            </h2>
            <p
              v-if="!isOnline"
              class="p-2 mb-4 text-sm text-center text-red-500 rounded-sm bg-white/20">
              You are offline. Please check your internet connection.
            </p>
          </template>
        </div>

        <template v-if="!showSuccess">
          <div class="mt-4 sm:mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
            <div class="mb-4">
              <ul class="flex justify-center space-x-4 text-white select-none">
                <li>
                  <a
                    href="#"
                    class="px-2 py-1 rounded-sm cursor-pointer hover:underline bg-white/5"
                    :class="[loginMode === 'email' ? 'bg-white/25' : '']"
                    @click.prevent="changeLoginMode('email')"
                    >Email</a
                  >
                </li>
                <li>
                  <a
                    href="#"
                    class="px-2 py-1 rounded-sm cursor-pointer hover:underline bg-white/5"
                    :class="[loginMode === 'phone' ? 'bg-white/25' : '']"
                    @click.prevent="changeLoginMode('phone')"
                    >Phone</a
                  >
                </li>
                <!-- <li>
                    <a
                      href="#"
                      class="px-2 py-1 rounded-sm cursor-pointer hover:underline bg-white/5"
                      :class="[loginMode === 'social' ? 'bg-white/25' : '']"
                      @click.prevent="loginMode = 'social'"
                      >Social</a
                    >
                  </li> -->
              </ul>
            </div>
            <div v-if="loginMode === 'email'">
              <form
                v-if="!showCheckEmail"
                class="space-y-6"
                action="#"
                method="POST"
                @submit.prevent="onSubmit">
                <div>
                  <label
                    for="email"
                    class="block text-sm font-medium leading-6 text-white"
                    >Email address</label
                  >
                  <div class="mt-2">
                    <input
                      id="email"
                      v-model="credentials.email"
                      name="email"
                      type="email"
                      autocomplete="email"
                      required=""
                      :class="[
                        'block py-1.5 w-full text-white rounded-md border-0 ring-1 ring-inset bg-white/5 shadow-xs focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6',
                        noAccountFound
                          ? 'ring-white/30 focus:ring-white/50'
                          : 'ring-white focus:ring-indigo-500'
                      ]"
                      @input="noAccountFound = false" />
                  </div>

                  <!-- No account found inline message -->
                  <div
                    v-if="noAccountFound"
                    class="p-3 mt-3 border rounded-lg border-white/10 bg-white/5">
                    <div class="flex items-start space-x-3">
                      <div
                        class="flex items-center justify-center flex-shrink-0 w-6 h-6 rounded-full bg-white/10">
                        <fa-icon
                          :icon="['fas', 'user-slash']"
                          class="text-xs text-white/50" />
                      </div>
                      <div class="flex-1 text-sm">
                        <p class="font-medium text-white/90">
                          No account found
                        </p>
                        <p class="mt-1 text-white/50">
                          We couldn't find an account with this email.
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- Default helper text -->
                  <p v-else class="mt-2 text-sm text-center text-gray-200">
                    We'll send a login code to your email
                  </p>
                </div>

                <div v-if="route.name !== 'register'">
                  <button
                    id="submit-button"
                    type="submit"
                    :disabled="!isOnline"
                    class="flex justify-center items-center px-3 py-1.5 w-full text-sm leading-6 text-white bg-transparent rounded-full border cursor-pointer hover:bg-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black disabled:bg-gray-500 disabled:text-gray-300 disabled:cursor-not-allowed"
                    @click.prevent="onSubmit">
                    <fa-icon
                      v-if="!loading"
                      :icon="['fas', 'arrow-right']"
                      class="ml-2" />
                    <fa-icon v-else icon="spinner-third" spin />
                  </button>
                </div>
              </form>
            </div>

            <!-- Phone Login -->
            <div v-if="loginMode === 'phone' && !showCheckPhone">
              <form class="space-y-6" @submit.prevent="onSubmitPhone">
                <div>
                  <label
                    for="phone"
                    class="block text-sm font-medium leading-6 text-white">
                    Phone Number
                  </label>
                  <div class="flex mt-2 space-x-1">
                    <button
                      type="button"
                      :class="[
                        'flex items-center gap-1 shrink-0 px-2 py-1.5 text-white rounded-md ring-1 ring-inset bg-white/5 focus:ring-2 focus:ring-inset',
                        noAccountFound
                          ? 'ring-white/30 focus:ring-white/50'
                          : 'ring-white focus:ring-indigo-500'
                      ]"
                      @click="showCountryPicker = true">
                      <span>+{{ credentials.phoneCountry }}</span>
                      <fa-icon :icon="['fas', 'chevron-down']" class="text-xs text-gray-400" />
                    </button>
                    <input
                      id="phone"
                      v-model="credentials.phone"
                      type="tel"
                      required
                      :class="[
                        'py-1.5 w-full text-white rounded-md ring-1 ring-inset bg-white/5 focus:ring-2 focus:ring-inset',
                        noAccountFound
                          ? 'ring-white/30 focus:ring-white/50'
                          : 'ring-white focus:ring-indigo-500'
                      ]"
                      @input="noAccountFound = false" />
                  </div>

                  <!-- No account found inline message -->
                  <div
                    v-if="noAccountFound"
                    class="p-3 mt-3 border rounded-lg border-white/10 bg-white/5">
                    <div class="flex items-start space-x-3">
                      <div
                        class="flex items-center justify-center flex-shrink-0 w-6 h-6 rounded-full bg-white/10">
                        <fa-icon
                          :icon="['fas', 'user-slash']"
                          class="text-xs text-white/50" />
                      </div>
                      <div class="flex-1 text-sm">
                        <p class="font-medium text-white/90">
                          No account found
                        </p>
                        <p class="mt-1 text-white/50">
                          We couldn't find an account with this phone number.
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- Default helper text -->
                  <p v-else class="mt-2 text-sm text-center text-gray-200">
                    We'll send a one-time PIN to your phone.
                  </p>
                </div>
                <div v-if="route.name !== 'register'">
                  <button
                    type="submit"
                    :disabled="!isOnline"
                    class="flex justify-center items-center px-3 py-1.5 w-full text-sm leading-6 text-white bg-transparent rounded-full border hover:bg-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black disabled:bg-gray-500 disabled:text-gray-300 disabled:cursor-not-allowed">
                    Submit
                  </button>
                </div>
              </form>
            </div>

            <!-- OTP Verification -->
            <div v-if="showCheckPhone" class="my-4">
              <form class="space-y-6" @submit.prevent="onSubmitOTP">
                <label class="block text-sm font-medium leading-6 text-white">
                  Verification Code
                </label>
                <div class="relative">
                  <!-- Visual boxes -->
                  <div class="relative flex justify-center gap-3">
                    <div
                      v-for="(digit, index) in 6"
                      :key="index"
                      class="flex items-center justify-center w-10 h-10 text-2xl font-bold text-center rounded-sm pointer-events-none select-none ring-1 ring-inset md:w-14 md:h-14 bg-white/10 cursor-text"
                      :class="{
                        'ring-white': index !== focusedIndex,
                        'ring-2 ring-indigo-500': index === focusedIndex
                      }">
                      {{ otpValue[index] || '' }}
                    </div>
                    <!-- Hidden single input for iOS autofill positioned over the boxes -->
                    <input
                      ref="otpSingleInput"
                      v-model="otpValue"
                      type="tel"
                      inputmode="numeric"
                      pattern="[0-9]{6}"
                      maxlength="6"
                      autocomplete="one-time-code"
                      class="absolute inset-0 z-10 w-full h-full opacity-0 cursor-text"
                      @beforeinput="handleOtpBeforeInput"
                      @input="handleSingleOtpInput"
                      @paste="handleSingleOtpPaste"
                      @focus="focusedIndex = Math.min(otpValue.length, 5)"
                      @blur="focusedIndex = -1" />
                  </div>
                </div>
                <p class="mt-2 text-sm text-center text-gray-200">
                  Enter the 6-digit code sent to your
                  <template v-if="loginMode === 'email'">email</template
                  ><template v-else>phone</template>
                </p>
                <p
                  v-if="isInOtpCooldown"
                  class="mt-1 text-xs text-center text-indigo-300">
                  Your code is valid for the next few minutes
                </p>
                <button
                  type="submit"
                  class="px-3 py-1.5 mt-4 w-full text-sm text-white bg-transparent rounded-full border hover:bg-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black">
                  <fa-icon v-if="loading" icon="spinner-third" spin />
                  <span v-else>Verify</span>
                </button>
              </form>
            </div>

            <div v-else-if="route.name === 'register'" class="my-2 sm:my-4">
              <div
                class="p-4 text-sm text-center text-white rounded-lg sm:my-4 border border-white/10 bg-white/5">
                <!-- Referral Code Input -->
                <div class="mb-2 space-y-2 text-left sm:mb-4">
                  <label class="block text-sm font-medium text-white">
                    Invite code (optional)
                  </label>
                  <input
                    v-model="referralCode"
                    type="text"
                    placeholder="Invite code"
                    maxlength="8"
                    class="w-full px-3 py-2 text-center text-white uppercase border rounded-md bg-white/10 border-white/20 focus:ring-2 focus:ring-indigo-500 focus:border-transparent placeholder:text-white/80 placeholder:capitalize focus:placeholder:text-white/50"
                    :class="{
                      'border-green-500':
                        referralValidated && storeReferrals.isValidationValid,
                      '!border-red-600 focus:ring-red-600':
                        storeReferrals.validationError
                    }"
                    @input="handleReferralCodeInput" />

                  <!-- Validation feedback with fixed height -->
                  <div class="flex items-center justify-center h-8 sm:h-12">
                    <div
                      v-if="storeReferrals.validationLoading"
                      class="text-xs text-center text-gray-300">
                      <fa-icon icon="spinner-third" spin /> Validating...
                    </div>

                    <div
                      v-else-if="storeReferrals.validationError"
                      class="text-xs text-center text-gray-300">
                      {{ storeReferrals.validationError }}
                    </div>

                    <div
                      v-else-if="storeReferrals.isValidationValid"
                      class="text-xs text-center text-green-300">
                      <fa-icon icon="check" /> Valid! Invited by @{{
                        storeReferrals.validationReferrer?.username
                      }}
                      <br />
                      <span class="text-gray-300"
                        >{{ storeReferrals.validationRemainingUses }} uses
                        remaining</span
                      >
                    </div>
                    <div v-else class="text-xs text-center text-gray-300">
                      <p>
                        Got a code from a member? Add it and you'll follow
                        each other from the start.
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  :disabled="inviteBlocksSubmit"
                  :class="[
                    'w-full px-3 cursor-pointer text-sm py-1.5 sm:mt-4 mt-2 rounded-full text-white disabled:opacity-30 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black',
                    !inviteBlocksSubmit &&
                    ((loginMode === 'email' && credentials.email) ||
                      (loginMode === 'phone' && credentials.phone))
                      ? 'bg-gradient-to-r from-sky-500 to-indigo-500 gap-x-2 hover:from-sky-400 hover:to-indigo-400'
                      : 'border bg-transparent hover:bg-indigo-400 disabled:hover:bg-transparent'
                  ]"
                  @click.prevent="handleRegister">
                  <fa-icon
                    v-if="storeReferrals.redeemLoading"
                    icon="spinner-third"
                    spin />
                  <span v-else
                    >Join Spectra <fa-icon icon="arrow-right" class="ml-1"
                  /></span>
                </button>

                <div
                  v-if="storeReferrals.redeemError"
                  class="mt-2 text-xs text-center text-red-300">
                  {{ storeReferrals.redeemError }}
                </div>
              </div>

              <!-- Terms text outside of the signup block -->
              <p class="mt-2 text-xs italic text-center text-white/80">
                By joining you accept our
                <a
                  href="https://spectra.reviews/terms"
                  target="_blank"
                  class="underline cursor-hover"
                  >terms and conditions</a
                >.
              </p>
            </div>
            <template v-if="storeAuth.authError?.code">
              <div v-if="storeAuth.authError.code === 'otp_disabled'"></div>
              <!-- <div class="p-2 my-4 text-sm text-center text-red-200" v-else>
                Sorry there was a problem logging you in:

                <br />
                <span class="space-x-2">
                  <span class="font-bold">{{ storeAuth.authError.code }}</span
                  ><span>{{ storeAuth.authError.message }}</span>
                </span>
              </div> -->
            </template>

            <!-- Auth toggle -->
            <div class="mt-6 text-sm text-center">
              <template v-if="route.name === 'register'">
                <span class="text-gray-300"> Already have an account? </span>
                <router-link
                  :to="{ name: 'auth', query: authToggleQuery }"
                  class="ml-1 text-blue-400 transition-colors hover:text-blue-300">
                  Sign in instead
                </router-link>
              </template>
              <template v-else>
                <span class="text-gray-300"> Don't have an account? </span>
                <router-link
                  :to="{ name: 'register', query: authToggleQuery }"
                  class="ml-1 text-blue-400 transition-colors hover:text-blue-300">
                  Create account
                </router-link>
              </template>
            </div>
            <div
              v-if="storeAuth.authDebug && false"
              class="fixed bottom-0 flex flex-col max-w-sm px-6 py-4 my-4 space-y-2 text-sm rounded-sm bg-white/5">
              <p class="font-bold">
                <fa-icon icon="triangle-exclamation" class="mr-1" />
                Auth debug requested:
              </p>
              <p class="italic">
                {{ storeAuth.authDebug }}
              </p>
            </div>

            <!-- OTP/Auth Error Display -->
            <div v-if="formErrors" class="mt-6">
              <div
                class="flex flex-col p-4 space-y-3 text-sm border rounded-lg border-red-500/30 bg-red-500/10">
                <div class="flex items-start space-x-3">
                  <div
                    class="flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-full bg-red-500/20">
                    <fa-icon
                      :icon="['fas', 'triangle-exclamation']"
                      class="text-red-400" />
                  </div>
                  <div class="flex-1">
                    <p class="font-medium text-red-300">
                      {{ formErrors }}
                    </p>
                  </div>
                </div>
                <div class="flex gap-2 pt-2">
                  <button
                    type="button"
                    class="flex-1 px-3 py-2 text-xs font-medium text-gray-300 transition-colors bg-gray-800 rounded-md hover:bg-gray-700"
                    @click="formErrors = null">
                    Dismiss
                  </button>
                  <button
                    type="button"
                    :disabled="isInOtpCooldown"
                    class="flex-[2] px-3 py-2 text-xs font-medium text-white bg-indigo-600 rounded-md transition-colors hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    @click="resetForm">
                    <template v-if="isInOtpCooldown">
                      Wait {{ otpCooldownRemaining }}s
                    </template>
                    <template v-else>Request New Code</template>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
            <div
              class="flex flex-col max-w-sm px-6 py-4 my-4 space-y-2 text-sm rounded-sm bg-white/10">
              <p class="mb-4 text-xl font-bold text-center">
                Logged in <fa-icon icon="check" class="ml-1" />
              </p>

              <template v-if="showSuccessAutoRedirect">
                <a
                  href="/"
                  class="italic text-center opacity-75 cursor-pointer hover:underline"
                  >Launching <fa-icon icon="spinner-third" class="ml-1" spin
                /></a>
              </template>
              <template v-else>
                <a
                  href="/"
                  class="italic text-center opacity-75 cursor-pointer hover:underline"
                  >Launch Spectra
                  <fa-icon icon="rocket" class="group-hover:animate-bounce"
                /></a>
              </template>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Country code picker sheet -->
    <Sheet v-model="showCountryPicker" title="Select Country" size="lg" content-class="">
      <div class="sticky top-0 z-10 px-4 pt-2 pb-3 bg-gray-900">
        <div class="relative">
          <fa-icon
            :icon="['fas', 'search']"
            class="absolute text-gray-400 -translate-y-1/2 left-3 top-1/2" />
          <input
            ref="countrySearchInput"
            v-model="countrySearch"
            type="text"
            placeholder="Search country or dial code..."
            class="w-full py-2.5 pl-9 pr-3 text-sm text-white rounded-lg bg-white/10 ring-1 ring-inset ring-white/20 focus:ring-2 focus:ring-indigo-500 focus:outline-none placeholder:text-gray-400" />
        </div>
      </div>
      <ul>
        <li
          v-if="filteredCountries.length === 0"
          class="px-4 py-3 text-sm text-gray-400">
          No results
        </li>
        <li
          v-for="country in filteredCountries"
          :key="country.code"
          class="px-4 py-3 text-sm text-white border-b cursor-pointer border-white/5 active:bg-white/10"
          :class="credentials.phoneCountry === country.dialCode ? 'bg-indigo-600/20' : ''"
          @click="selectCountry(country)">
          <span class="font-medium">{{ country.name }}</span>
          <span class="ml-2 text-gray-400">(+{{ country.dialCode }})</span>
        </li>
      </ul>
    </Sheet>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  computed,
  reactive,
  watch,
  onMounted,
  onUnmounted,
  inject
} from 'vue'
import { useNetworkStatus } from '@/use/useNetworkStatus'
import { useStoreAuth } from '@/stores/storeAuth'
import { useStoreReferrals } from '@/stores/storeReferrals'
import { useRoute, useRouter } from 'vue-router'
import { useHapticFeedback } from '@/composables/useHapticFeedback'
import { Sheet } from '@/components/Options'
import { countryCodes } from '@/data/countryCodes'

const props = defineProps({
  context: {
    type: String,
    default: 'modal'
  }
})
const emit = defineEmits(['complete'])

const { isNativeApp } = inject('platform')
const storeAuth = useStoreAuth()
const storeReferrals = useStoreReferrals()
const route = useRoute()
const router = useRouter()
const haptics = useHapticFeedback()
const register = ref(false)
const showCheckEmail = ref(false)
const showCheckPhone = ref(false)
const showSignup = ref(false)
const loading = ref(false)
const loginMode = ref('email') // 'email' or 'phone'
const allowUserCreate = ref(false)

const formErrors = ref(null) // Track form-related errors
const noAccountFound = ref(false) // Track "no account found" state for inline display
const credentials = reactive({
  email: '',
  phoneCountry: '44',
  phone: '',
  otp: ''
})

// Country code picker
const showCountryPicker = ref(false)
const countrySearch = ref('')
const countrySearchInput = ref(null)
const pinnedCodes = ['GB', 'US']
const filteredCountries = computed(() => {
  const list = !countrySearch.value
    ? countryCodes
    : countryCodes.filter((c) => {
        const q = countrySearch.value.toLowerCase()
        return (
          c.name.toLowerCase().includes(q) ||
          c.dialCode.includes(q) ||
          ('+' + c.dialCode).includes(q)
        )
      })
  const pinned = pinnedCodes
    .map((code) => list.find((c) => c.code === code))
    .filter(Boolean)
  const rest = list.filter((c) => !pinnedCodes.includes(c.code))
  return [...pinned, ...rest]
})
const selectCountry = (country) => {
  credentials.phoneCountry = country.dialCode
  noAccountFound.value = false
  showCountryPicker.value = false
  countrySearch.value = ''
}

// Referral code state
const referralCode = ref('')
const showReferralInput = ref(false)
const referralValidated = ref(false)
const referralValidationTimer = ref(null)

// OTP request rate limiting
const OTP_COOLDOWN_MS = 60000 // 60 seconds - matches Supabase rate limit
const lastOtpRequestTime = ref(0)
const otpCooldownRemaining = ref(0)
let otpCooldownInterval = null

// Start cooldown timer after OTP request
const startOtpCooldown = () => {
  lastOtpRequestTime.value = Date.now()
  otpCooldownRemaining.value = Math.ceil(OTP_COOLDOWN_MS / 1000)

  // Store in sessionStorage to persist across page refreshes
  sessionStorage.setItem('lastOtpRequestTime', lastOtpRequestTime.value.toString())

  // Clear any existing interval
  if (otpCooldownInterval) clearInterval(otpCooldownInterval)

  // Update countdown every second
  otpCooldownInterval = setInterval(() => {
    const elapsed = Date.now() - lastOtpRequestTime.value
    const remaining = Math.ceil((OTP_COOLDOWN_MS - elapsed) / 1000)
    otpCooldownRemaining.value = Math.max(0, remaining)

    if (otpCooldownRemaining.value <= 0) {
      clearInterval(otpCooldownInterval)
      otpCooldownInterval = null
    }
  }, 1000)
}

// Check if still in cooldown period
const isInOtpCooldown = computed(() => otpCooldownRemaining.value > 0)

const otpValue = ref('') // Single string for OTP value
const otpSingleInput = ref(null) // Ref for the single hidden input
const focusedIndex = ref(-1) // Track which visual box should appear focused
const formTitle = computed(() =>
  route.name === 'register' ? 'Register' : 'Login'
)

// Query params for auth toggle links - preserves entered email/phone
const authToggleQuery = computed(() => {
  const query = { ...route.query }
  if (loginMode.value === 'email' && credentials.email) {
    query.email = credentials.email
    query.mode = 'email'
  } else if (loginMode.value === 'phone' && credentials.phone) {
    query.phone = credentials.phone
    query.phoneCountry = credentials.phoneCountry
    query.mode = 'phone'
  }
  // Include step if in OTP mode
  if (showCheckEmail.value || showCheckPhone.value) {
    query.step = 'otp'
  }
  return query
})

// Update URL query params to persist auth state
const updateQueryParams = () => {
  const query = { ...route.query }

  // Set mode and credentials
  if (loginMode.value === 'email' && credentials.email) {
    query.email = credentials.email
    query.mode = 'email'
  } else if (loginMode.value === 'phone' && credentials.phone) {
    query.phone = credentials.phone
    query.phoneCountry = credentials.phoneCountry
    query.mode = 'phone'
  }

  // Set step for OTP view
  if (showCheckEmail.value || showCheckPhone.value) {
    query.step = 'otp'
  } else {
    delete query.step
  }

  // Update URL without navigation
  router.replace({ query })
}
const { isOnline } = useNetworkStatus()
const showSuccess = ref(false)
const showSuccessAutoRedirect = ref(false)

// Back button functionality
const hasRedirect = computed(() => !!route.query.redirect)

// Back button functionality
const goBack = () => {
  // First check sessionStorage for the stored route (route before hitting auth requirement)
  const storedRoute = sessionStorage.getItem('authRedirectRoute')

  if (storedRoute) {
    // Clear the stored route since we're navigating away
    sessionStorage.removeItem('authRedirectRoute')
    router.push(storedRoute)
  } else if (route.query.redirect) {
    // Fallback to query param redirect if no stored route
    const redirect = Array.isArray(route.query.redirect)
      ? route.query.redirect[0]
      : route.query.redirect
    router.push(redirect || '/')
  } else {
    // Final fallback: go to root path
    router.push('/')
  }
}

watch(register, () => storeAuth.clear())

// Auto-focus OTP input when form appears and update query params
watch(showCheckPhone, (newValue) => {
  if (newValue) {
    setTimeout(() => {
      otpSingleInput.value?.focus()
    }, 100)
    updateQueryParams()
  }
})

watch(showCheckEmail, (newValue) => {
  if (newValue) {
    setTimeout(() => {
      otpSingleInput.value?.focus()
    }, 100)
    updateQueryParams()
  }
})

// Reset form state
const resetForm = () => {
  showSuccess.value = false
  showCheckEmail.value = false
  showCheckPhone.value = false
  showSignup.value = false
  allowUserCreate.value = false
  credentials.email = ''
  credentials.phone = ''
  credentials.otp = ''
  otpValue.value = '' // Reset OTP value
  formErrors.value = null
  noAccountFound.value = false
  // Reset referral code state
  referralCode.value = ''
  showReferralInput.value = false
  referralValidated.value = false
  if (referralValidationTimer.value) {
    clearTimeout(referralValidationTimer.value)
    referralValidationTimer.value = null
  }
  storeReferrals.clearValidation()
  storeReferrals.clearRedeemError()

  // Clear step from URL query params
  const query = { ...route.query }
  delete query.step
  delete query.email
  delete query.phone
  delete query.phoneCountry
  delete query.mode
  router.replace({ query })

  // Check for referral code in URL query parameters after reset
  checkForReferralCodeFromQuery()
}

// Change login mode (email or phone)
const changeLoginMode = (mode) => {
  loginMode.value = mode
  resetForm()
}

// Check for referral code in URL query parameters
const checkForReferralCodeFromQuery = () => {
  if (route.query.ref) {
    referralCode.value = route.query.ref.toString().toUpperCase()
    validateReferralCode()
  }
}

// Referral code handling
const handleReferralCodeInput = (event) => {
  const value = event.target.value.toUpperCase()
  referralCode.value = value
  referralValidated.value = false

  // Clear any pending validation timer
  if (referralValidationTimer.value) {
    clearTimeout(referralValidationTimer.value)
    referralValidationTimer.value = null
  }

  if (value.length === 0) {
    storeReferrals.clearValidation()
    return
  }

  // Debounce validation - wait 500ms after user stops typing
  referralValidationTimer.value = setTimeout(() => {
    validateReferralCode()
  }, 500)
}

// The register step accepts an empty code. A code that is typed has to be
// one the server accepted before the OTP is requested, so a typo does not
// quietly register an account with no inviter behind it.
const inviteBlocksSubmit = computed(
  () =>
    referralCode.value.trim() !== '' &&
    !(referralValidated.value && storeReferrals.isValidationValid)
)

const inviteRefusedMessage =
  'That invite code was not accepted. Fix it, or clear it to continue.'

const validateReferralCode = async () => {
  if (!referralCode.value.trim()) {
    referralValidated.value = false
    return
  }

  const result = await storeReferrals.validateReferralCode(referralCode.value)
  referralValidated.value = !!result
}

// Handle registration
const handleRegister = async () => {
  if (inviteBlocksSubmit.value) {
    alert(inviteRefusedMessage)
    return
  }

  allowUserCreate.value = true
  if (loginMode.value === 'email') onSubmit()
  if (loginMode.value === 'phone') onSubmitPhone()
}

// Handle email login submission
const onSubmit = async () => {
  formErrors.value = null
  noAccountFound.value = false

  if (!credentials.email) {
    alert('Enter an email address first')
    return
  }

  // On the register route a typed invite code has to have been accepted;
  // no code at all is fine.
  if (route.name === 'register') {
    if (inviteBlocksSubmit.value) {
      alert(inviteRefusedMessage)
      return
    }
    // Set allowUserCreate for registration
    allowUserCreate.value = true
  }

  loading.value = true
  try {
    await storeAuth.handleLoginEmail(credentials.email, allowUserCreate.value)
    showSignup.value = false
    showCheckEmail.value = true
    showCheckPhone.value = true
    startOtpCooldown() // Start rate limit cooldown
  } catch (err) {
    if (err.code !== 'otp_disabled') {
      // Check if this is a "no account found" error (422 status from Supabase)
      const isNoAccountError =
        err.message?.toLowerCase().includes('no account found') ||
        err.status === 422

      if (isNoAccountError) {
        noAccountFound.value = true
      } else {
        formErrors.value =
          err.message || 'An error occurred while sending the email.'
      }
    } else {
      showSignup.value = true
    }
  } finally {
    loading.value = false
  }
}

// Handle phone login submission
const onSubmitPhone = async () => {
  formErrors.value = null
  noAccountFound.value = false

  if (!credentials.phone) {
    alert('Enter your phone number.')
    return
  }

  // On the register route a typed invite code has to have been accepted;
  // no code at all is fine.
  if (route.name === 'register') {
    if (inviteBlocksSubmit.value) {
      alert(inviteRefusedMessage)
      return
    }
    // Set allowUserCreate for registration
    allowUserCreate.value = true
  }

  loading.value = true
  try {
    await storeAuth.handleLoginPhone(
      credentials.phoneCountry,
      credentials.phone,
      allowUserCreate.value
    )
    showSignup.value = false
    showCheckPhone.value = true
    startOtpCooldown() // Start rate limit cooldown
  } catch (err) {
    if (err.code !== 'otp_disabled') {
      // Check if this is a "no account found" error (422 status from Supabase)
      const isNoAccountError =
        err.message?.toLowerCase().includes('no account found') ||
        err.status === 422

      if (isNoAccountError) {
        noAccountFound.value = true
      } else {
        formErrors.value =
          err.message || 'An error occurred while sending the OTP.'
      }
    } else {
      showSignup.value = true
    }
  } finally {
    loading.value = false
  }
}

// Handle beforeinput to catch iOS autofill before maxlength truncates it
// iOS autofill fires beforeinput with inputType 'insertReplacementText'
// Regular paste uses 'insertFromPaste' - we let that go through to the paste handler
const handleOtpBeforeInput = (e) => {
  // Let paste events go through to the paste handler
  if (e.inputType === 'insertFromPaste') {
    return // Don't interfere - paste handler will take care of it
  }

  // Check if this is iOS autofill (insertReplacementText) or multiple digits from other sources
  const incomingData = e.data || ''
  const digits = incomingData.replace(/\D/g, '')

  // Only intercept multi-digit input that isn't a paste (autofill scenario)
  if (digits.length > 1) {
    e.preventDefault()
    const otp = digits.slice(0, 6)
    otpValue.value = otp
    focusedIndex.value = Math.min(otp.length, 5)
    haptics.light()

    if (otp.length === 6) {
      haptics.medium()
      onSubmitOTP()
    }
  }
}

// New OTP event handlers for single input approach
const handleSingleOtpInput = (e) => {
  const value = e.target.value.replace(/\D/g, '') // Remove non-digits
  const prevLength = otpValue.value.length
  const newValue = value.slice(0, 6) // Ensure max 6 digits

  // Update the value
  otpValue.value = newValue
  focusedIndex.value = Math.min(newValue.length, 5)

  // Haptic feedback for new digits (including paste scenarios)
  if (newValue.length > prevLength) {
    haptics.light()
  }

  // Auto-submit when OTP is complete
  if (newValue.length === 6) {
    haptics.medium()
    onSubmitOTP()
  }
}

const handleSingleOtpPaste = (e) => {
  // Try to get pasted text from clipboard
  let text = ''

  // Standard way
  if (e.clipboardData && e.clipboardData.getData) {
    text = e.clipboardData.getData('text')
  }
  // Fallback for older browsers
  else if (window.clipboardData && window.clipboardData.getData) {
    text = window.clipboardData.getData('Text')
  }

  // If we got text, handle it
  if (text) {
    e.preventDefault()
    const digits = text.trim().replace(/\D/g, '') // Remove non-digits
    if (digits.length > 0 && digits.length <= 6) {
      otpValue.value = digits.slice(0, 6)
      focusedIndex.value = Math.min(otpValue.value.length, 5)
      haptics.light()
      // Auto-submit when OTP is complete
      if (otpValue.value.length === 6) {
        haptics.medium()
        onSubmitOTP()
      }
    }
  }
  // If we couldn't get clipboard data, don't prevent default
  // This allows the browser's native paste to work
}

// Get user-friendly error message for OTP errors
const getOTPErrorMessage = (error) => {
  if (!error) return 'Invalid code. Please try again.'

  const code = error.code?.toLowerCase() || ''
  const message = error.message?.toLowerCase() || ''

  // Token already used
  if (
    code === 'otp_expired' ||
    code.includes('expired') ||
    message.includes('expired')
  ) {
    return 'This code has expired. Please request a new one.'
  }

  // Token already redeemed/used
  if (
    code === 'token_hash_not_found' ||
    message.includes('token') ||
    message.includes('invalid') ||
    message.includes('already')
  ) {
    return 'This code has already been used or is invalid. Please request a new one.'
  }

  // Rate limiting
  if (code === 'over_request_rate_limit' || message.includes('rate')) {
    return 'Too many attempts. Please wait a moment and try again.'
  }

  // Fallback to original message if available
  return error.message || 'Invalid code. Please try again.'
}

// Handle OTP submission
const onSubmitOTP = async () => {
  credentials.otp = otpValue.value
  if (credentials.otp.length !== 6) {
    alert('Please complete the 6-digit verification code.')
    return
  }

  loading.value = true
  formErrors.value = null
  try {
    const session = await storeAuth.handleLoginOTP(loginMode.value, credentials, {
      skipInit: props.context === 'page'
    })

    // If this was a registration with a referral code, redeem it
    if (allowUserCreate.value && referralCode.value && session?.user?.id) {
      try {
        await storeReferrals.redeemReferralCode(
          referralCode.value,
          session.user.id
        )
      } catch (referralError) {
        console.error('Failed to redeem referral code:', referralError)
        // Don't fail the login process if referral redemption fails
      }
    }

    haptics.success()
    handleShowSuccess()
  } catch (err) {
    haptics.error()
    formErrors.value = getOTPErrorMessage(err)
    // Clear OTP fields so user can enter a fresh code
    otpValue.value = ''
    // Focus the input field
    setTimeout(() => {
      otpSingleInput.value?.focus()
    }, 100)
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  // Restore credentials from query params (when switching between login/register)
  if (route.query.mode === 'email' && route.query.email) {
    loginMode.value = 'email'
    credentials.email = route.query.email
  } else if (route.query.mode === 'phone' && route.query.phone) {
    loginMode.value = 'phone'
    credentials.phone = route.query.phone
    if (route.query.phoneCountry) {
      credentials.phoneCountry = route.query.phoneCountry
    }
  }

  // Restore OTP cooldown from sessionStorage
  const storedOtpTime = sessionStorage.getItem('lastOtpRequestTime')
  if (storedOtpTime) {
    const elapsed = Date.now() - parseInt(storedOtpTime, 10)
    if (elapsed < OTP_COOLDOWN_MS) {
      lastOtpRequestTime.value = parseInt(storedOtpTime, 10)
      startOtpCooldown()
    }
  }

  // Restore OTP step state if present (e.g., user navigated away and came back)
  if (route.query.step === 'otp') {
    // Only restore OTP state if we have the credentials to verify against
    if (route.query.mode === 'email' && route.query.email) {
      showCheckEmail.value = true
      showCheckPhone.value = true
      // Focus OTP input after short delay
      setTimeout(() => {
        otpSingleInput.value?.focus()
      }, 100)
    } else if (route.query.mode === 'phone' && route.query.phone) {
      showCheckPhone.value = true
      // Focus OTP input after short delay
      setTimeout(() => {
        otpSingleInput.value?.focus()
      }, 100)
    }
  }

  // Check for referral code in URL query parameters
  checkForReferralCodeFromQuery()

  // Only allow signup mode on the register route
  if (route.name === 'register') {
    showSignup.value = true
    allowUserCreate.value = true
  }
})

onUnmounted(() => {
  // Clean up referral validation timer
  if (referralValidationTimer.value) {
    clearTimeout(referralValidationTimer.value)
  }
  // Clean up OTP cooldown interval
  if (otpCooldownInterval) {
    clearInterval(otpCooldownInterval)
  }
})

const handleShowSuccess = () => {
  showSuccess.value = true
  showSuccessAutoRedirect.value = true

  setTimeout(() => {
    showSuccessAutoRedirect.value = false
  }, 5000)

  // Post-login redirect: use query param (protected route they were trying to access)
  // The stored route is only for back button navigation, not post-login redirect
  const queryRedirect = Array.isArray(route.query.redirect)
    ? route.query.redirect[0]
    : route.query.redirect
  const storedRoute = sessionStorage.getItem('authRedirectRoute')
  // Prefer query param (where they were trying to go), fallback to root
  const redirectPath = queryRedirect || '/'

  // Redirect after successful login
  if (props.context === 'page') {
    setTimeout(() => {
      // Clear the stored route since we're redirecting
      if (storedRoute) {
        sessionStorage.removeItem('authRedirectRoute')
      }
      window.location.href = redirectPath
    }, 250)
  } else {
    setTimeout(() => {
      // Clear the stored route since we're completing
      if (storedRoute) {
        sessionStorage.removeItem('authRedirectRoute')
      }
      emit('complete')
    }, 250)
  }
}

const showHomeLink = computed(() => !isNativeApp.value)
</script>

<style>
.auth-form {
  max-width: 400px;
  margin: 0 auto;
}
</style>
